'use client';

import React, { useState, useCallback, useEffect } from 'react';

import config from 'configs/app';

import styles from './ConnectButton.module.css';

const CHAIN_ID_HEX = config.chain.id ? '0x' + parseInt(config.chain.id).toString(16) : '0x42F';

const ConnectButton: React.FC = () => {
  const [ address, setAddress ] = useState<string | null>(null);
  const [ isConnected, setIsConnected ] = useState(false);

  const formatAddress = (addr: string) => {
    return `${ addr.slice(0, 6) }...${ addr.slice(-4) }`;
  };

  const checkConnection = useCallback(async() => {
    if (typeof window === 'undefined' || !window.ethereum) {
      return;
    }
    try {
      const accounts = await window.ethereum.request({ method: 'eth_accounts' }) as Array<string>;
      if (accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);
      }
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    checkConnection();

    if (typeof window !== 'undefined' && window.ethereum) {
      const handleAccountsChanged = (accounts: Array<string>) => {
        if (accounts.length > 0) {
          setAddress(accounts[0]);
          setIsConnected(true);
        } else {
          setAddress(null);
          setIsConnected(false);
        }
      };

      window.ethereum.on?.('accountsChanged', handleAccountsChanged);
      return () => {
        window.ethereum?.removeListener?.('accountsChanged', handleAccountsChanged);
      };
    }
  }, [ checkConnection ]);

  const handleConnect = async() => {
    if (typeof window === 'undefined' || !window.ethereum) {
      alert('Please install MetaMask or another Web3 wallet.');
      return;
    }

    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' }) as Array<string>;
      if (accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);
      }

      // Try to switch to OpenGPU Mainnet
      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: CHAIN_ID_HEX }],
        });
      } catch (switchError: unknown) {
        // Chain not added, try to add it
        if (switchError && typeof switchError === 'object' && 'code' in switchError && (switchError as { code: number }).code === 4902) {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: CHAIN_ID_HEX,
              chainName: config.chain.name,
              nativeCurrency: {
                name: config.chain.currency.name,
                symbol: config.chain.currency.symbol,
                decimals: config.chain.currency.decimals,
              },
              rpcUrls: config.chain.rpcUrls,
              blockExplorerUrls: [ config.app.baseUrl ],
            }],
          });
        }
      }
    } catch {
      // User rejected
    }
  };

  const handleDisconnect = () => {
    setAddress(null);
    setIsConnected(false);
  };

  const handleClick = () => {
    if (isConnected) {
      handleDisconnect();
    } else {
      handleConnect();
    }
  };

  return (
    <button
      className={ styles.connectButton }
      onClick={ handleClick }
      type="button"
    >
      { isConnected && address ? formatAddress(address) : 'Connect Wallet' }
    </button>
  );
};

export default ConnectButton;
